import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Middleware } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';

import AppLogger from '../../common/logger/AppLogger';
import { appSubscriptionService, DispenseCompleteEvent } from '../../common/subscriptions/appSubscriptionService';
import { Transaction } from '../../features/api';
import { DispenseState } from '../../features/api/dispenseLogs/DispenseLog';
import { MenuVersion } from '../../features/api/menu/MenuVersion';
import {
  addOverdueOrderDevice,
  clearOperatorSession,
  decrementPendingDispenseCount,
  decrementReadyToServeCount,
  incrementPendingDispenseCount,
  incrementReadyToServeCount,
  setOutstandingDispenseCount,
  setPendingDispenseCount,
  setReadyToServeCount,
  setPollData,
  setSignalrStatus,
  setWorkstationBlocked,
  setWorkstationClosed,
} from '../../features/appState';
import { login } from '../../features/auth';
import { refreshToken } from '../../features/auth/oauth';
import { clearErrorDialog, showErrorDialog } from '../../features/error-dialog/errorDialogSlice';
import {
  setLayoutDefinitions,
  setMenuItemCondiments,
  setMenuVersion,
  setPackages,
  setTransaction,
  setUnitOfSales,
  updateStockLevel,
} from '../../features/order/orderSlice';
import { setTenderMedias } from '../../features/tender/tenderSlice';

import { RootState } from './reducer';

interface WebsocketResult<T> {
  success: boolean;
  error?: string;
  errorCode?: number;
  data: T;
}

// Helper function to fetch and update pending dispense count
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchPendingDispenseCount = async (store: any) => {
  try {
    const state = store.getState();
    const config = state.config.configuration;
    if (config?.api?.baseUrl && state.auth.token?.access_token) {
      const headers = {
        'x-api-version': '1.0',
        Authorization: `Bearer ${state.auth.token.access_token}`,
      };

      const [activeResponse, readyResponse, outstandingResponse] = await Promise.all([
        fetch(`${config.api.baseUrl}/dispense-logs/active`, { method: 'GET', headers }),
        fetch(`${config.api.baseUrl}/dispense-logs/ready-to-serve`, { method: 'GET', headers }),
        fetch(`${config.api.baseUrl}/dispense-logs/outstanding`, { method: 'GET', headers }),
      ]);

      if (activeResponse.ok) {
        const logs = await activeResponse.json();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pendingCount = logs.filter((log: any) => log.state !== DispenseState.Served).length;
        store.dispatch(setPendingDispenseCount(pendingCount));
        AppLogger.info(`Active dispense count (not served): ${pendingCount}`);
      }

      if (readyResponse.ok) {
        const readyLogs = await readyResponse.json();
        store.dispatch(setReadyToServeCount(readyLogs.length));
        AppLogger.info(`Ready to serve count: ${readyLogs.length}`);
      }

      if (outstandingResponse.ok) {
        const outstandingLogs = await outstandingResponse.json();
        store.dispatch(setOutstandingDispenseCount(outstandingLogs.length));
        AppLogger.info(`Outstanding dispense count: ${outstandingLogs.length}`);
      }
    }
  } catch (error) {
    AppLogger.error('Failed to fetch pending dispense count', error);
  }
};

// Refresh just the outstanding dispense count (orders originated from this workstation)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchOutstandingDispenseCount = async (store: any) => {
  try {
    const state = store.getState();
    const config = state.config.configuration;
    if (!config?.api?.baseUrl || !state.auth.token?.access_token) return;

    const response = await fetch(`${config.api.baseUrl}/dispense-logs/outstanding`, {
      method: 'GET',
      headers: {
        'x-api-version': '1.0',
        Authorization: `Bearer ${state.auth.token.access_token}`,
      },
    });
    if (response.ok) {
      const logs = await response.json();
      store.dispatch(setOutstandingDispenseCount(logs.length));
    }
  } catch (error) {
    AppLogger.error('Failed to refresh outstanding dispense count', error);
  }
};

// Track last processed state per dispense log to deduplicate events from multiple server groups
const lastProcessedDispenseState = new Map<number, string>();

// eslint-disable-next-line @typescript-eslint/ban-types
const signalrMiddleware: Middleware<{}, RootState> = (store) => {
  let connection: HubConnection | undefined = undefined;

  // Subscribe to alternate error button clicks to handle low quantity confirmation (OK button)
  appSubscriptionService.errorButtonAlternateClickedObservable().subscribe((state) => {
    if (state?.type === 'low-quantity-confirm' && state?.originalPayload) {
      // Re-dispatch the add item action with NegativeStockConfirmed flag
      const payload = state.originalPayload;
      store.dispatch({
        type: 'signalr/add-item-to-basket',
        payload: {
          transactionId: payload.transactionId,
          model: { ...payload.model, NegativeStockConfirmed: true },
        },
      });
    }
  });

  return (next) => (action) => {
    if (action.type === 'signalr/connect') {
      const getToken = async () => {
        const state = store.getState();
        if (state.auth.token) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const jwt = jwtDecode<any>(state.auth.token.access_token);
          if (jwt && jwt.exp) {
            const currentTime = new Date().getTime() / 1000;
            if (currentTime < jwt.exp) {
              if (state.config.configuration) {
                const refreshResult = await refreshToken(state.config.configuration.oAuth, state.auth.token.refresh_token);
                if (refreshResult !== undefined) {
                  store.dispatch(login(refreshResult));
                  return refreshResult.access_token;
                }
              }
            }
            return state.auth.token?.access_token;
          }
          return '';
        }
      };

      const config = store.getState().config.configuration;
      if (config !== undefined) {
        if (connection === undefined) {
          connection = new HubConnectionBuilder()
            .withUrl(`${config.api.baseUrl}/EngineHub`, {
              accessTokenFactory: getToken,
              transport: HttpTransportType.WebSockets,
              skipNegotiation: true,
            })
            .withAutomaticReconnect({
              nextRetryDelayInMilliseconds(): number | null {
                return 2000;
              },
            })
            .build();

          connection.on('Kill', (workstationId: number) => {
            AppLogger.warn('Kill requested for WS ID ' + workstationId);
            if (store.getState().appState.workstation?.id === workstationId) {
              AppLogger.warn('This workstation is dead');
              appSubscriptionService.killNext(true);
            }
          });
          connection.on('Restart', () => window.location.reload());
          connection.on('LogoutOperator', (operatorId: number) => {
            AppLogger.info('logout operator', operatorId);
            const currentOperatorSession = store.getState().appState.operatorSession;
            if (currentOperatorSession && currentOperatorSession.operator.id === operatorId) store.dispatch(clearOperatorSession());
          });
          connection.on('NotifyCardPaymentFault', (transactionId: number, transactionNumber: number, error: string) => {
            AppLogger.info('NotifyCardPaymentFault', transactionId, transactionNumber, error);
            store.dispatch(
              showErrorDialog({ message: `Card Payment failed for transaction ${transactionNumber} due to ${error}`, dismissible: true }),
            );
          });
          connection.on('NotifyPrinterFault', (printerName: string, transactionId: number, transactionNumber: number, error: string) => {
            AppLogger.info('onNotifyPrinterFault', printerName, transactionId, transactionNumber, error);
            store.dispatch(
              showErrorDialog({
                message: `Printer Offline!<br/>${printerName}<br/><br/>Transaction: ${transactionNumber}`,
                dismissible: true,
              }),
            );
          });
          connection.on('NotifyPrinterBackup', (printerName: string, transactionId: number, transactionNumber: number) => {
            AppLogger.info('onNotifyPrinterBackup', printerName, transactionId, transactionNumber);
            store.dispatch(
              showErrorDialog({
                message: `Warning! Printed to backup.<br/>${printerName} is offline.<br/><br/>Transaction: ${transactionNumber}`,
                dismissible: true,
              }),
            );
          });

          connection.on('PollServerState', (serverDate: string, menuVersion: number, layoutVersion: number, packageVersion: number) => {
            AppLogger.info(`SignalR Server State ${serverDate}`);
            store.dispatch(setPollData({ date: serverDate, layoutDefinitionVersion: layoutVersion, menuVersion, packageVersion }));
            appSubscriptionService.layoutVersionNext(layoutVersion);
          });

          connection.on(
            'CardPaymentSuccess',
            (transactionId: number, transactionNumber: number, sequenceNumber: number, result: string, transaction: Transaction | null) => {
              AppLogger.info(`Card Payment success result for ${transactionId} which is ${transactionNumber}/${sequenceNumber}`);
              appSubscriptionService.cardPaymentSuccessNext({ transactionId, result, transaction });
            },
          );

          connection.on(
            'CardSignatureRequest',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              operatorName: string,
              terminalId: string,
              verificationRequestId: number,
            ) => {
              AppLogger.info(`Card Signature request for ${transactionId} on ${terminalId}`);
              appSubscriptionService.cardSignatureNext({
                sequenceNumber: sequenceNumber,
                transactionNumber: transactionNumber,
                terminalId: terminalId,
                verificationRequestId: verificationRequestId,
              });
            },
          );

          connection.on(
            'CardPaymentFailure',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              operatorName: string,
              amount: number,
              reason: string,
              allowManualEntry: boolean,
              cardRequestId: number | null,
              pedName: string,
              pedTerminalId: string,
              operatorId: number,
            ) => {
              AppLogger.info(
                'Card Payment failure result for ' + transactionId + ' ' + JSON.stringify({ transactionId, cardRequestId, amount }),
              );
              const priceFormatter = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
              });

              let extraText = '';
              if (reason == 'TimedOut')
                extraText = '<br>Communication with the PDQ has failed, please check card receipt and record accordingly';
              if (allowManualEntry) {
                extraText = `<br/>Please check the ${pedName} card machine for the result of the payment of ${priceFormatter.format(
                  amount,
                )}.<br/>Did the payment succeed?`;
                appSubscriptionService.cardFailureLockNext({
                  transactionId,
                  transactionNumber,
                  sequenceNumber,
                  operatorId,
                  operatorName,
                  amount,
                  reason,
                  cardRequestId,
                  pedName,
                  pedTerminalId,
                  message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                    amount,
                  )}</b><br> Card Machine was <b>${pedName}</b><br><br>Card Result was ${reason}<br>${extraText}`,
                });
              } else {
                store.dispatch(
                  showErrorDialog({
                    message: `Card payment failure for ${transactionNumber}/${sequenceNumber}.<br><br> Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                      amount,
                    )}</b><br><br>Card Result was ${reason}<br>${extraText}`,
                    dismissible: true,
                    buttonText: 'Acknowledge',
                    backgroundColor: 'red',
                    requireConfirmationOnClose: true,
                    confirmationText: 'The card payment looks to have failed. Please confirm this and ensure that the card machine is OK',
                  }),
                );
              }
              appSubscriptionService.cardPaymentFailureNext(transactionId);
            },
          );
          connection.on('CardPaymentStatus', (transactionId: number, notification: string, terminalId: string) => {
            AppLogger.info(`Card Payment Status result for ${transactionId} which is ${notification} on terminal ${terminalId}`);
            appSubscriptionService.cardNotificationNext({ transactionId, notification, terminalId });
          });

          connection.on(
            'CardPreAuthSuccess',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              amountAuthorized: number,
              authCode: string | null,
              cardScheme: string | null,
              expiresAt: string | null,
              cardRequestId: number | null,
              transaction: Transaction | null,
            ) => {
              AppLogger.info(
                `Card Pre-Auth success for ${transactionId} (${transactionNumber}/${sequenceNumber}), authorized £${amountAuthorized}`,
              );
              const preAuth = transaction?.transactionPreAuthorisations?.find((p) => p.cardRequestId === cardRequestId);
              appSubscriptionService.cardPreAuthSuccessNext({
                transactionId,
                preAuthId: preAuth?.id ?? null,
                amountAuthorized,
                authCode,
                cardScheme,
                expiresAt,
                cardRequestId,
                transaction,
              });
            },
          );

          connection.on(
            'CardPreAuthFailure',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              operatorName: string,
              amount: number,
              reason: string,
              cardRequestId: number | null,
              pedName: string,
              pedTerminalId: string,
              operatorId: number,
            ) => {
              AppLogger.info(`Card Pre-Auth failure for ${transactionId}: ${reason}`);
              const priceFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
              store.dispatch(
                showErrorDialog({
                  message: `Pre-authorization failure for ${transactionNumber}/${sequenceNumber}.<br><br>Operator was <b>${operatorName}</b><br>Value <b>${priceFormatter.format(
                    amount,
                  )}</b><br>Card Machine was <b>${pedName}</b><br><br>Result: ${reason}`,
                  dismissible: true,
                  buttonText: 'Acknowledge',
                  backgroundColor: 'red',
                }),
              );
              appSubscriptionService.cardPreAuthFailureNext({
                transactionId,
                transactionNumber,
                sequenceNumber,
                operatorId,
                operatorName,
                amount,
                reason,
                cardRequestId,
                pedName,
                pedTerminalId,
                message: reason,
              });
            },
          );

          connection.on(
            'CardPreAuthCaptured',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              preAuthId: number,
              amountCaptured: number,
              transaction: Transaction | null,
            ) => {
              AppLogger.info(`Pre-Auth captured for ${transactionId} (${transactionNumber}/${sequenceNumber}), £${amountCaptured}`);
              appSubscriptionService.cardPreAuthCapturedNext({ transactionId, preAuthId, amountCaptured, transaction });
            },
          );

          connection.on(
            'CardPreAuthIncremented',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              preAuthId: number,
              newAmountAuthorized: number,
              transaction: Transaction | null,
            ) => {
              AppLogger.info(
                `Pre-Auth incremented for ${transactionId} (${transactionNumber}/${sequenceNumber}), now £${newAmountAuthorized}`,
              );
              appSubscriptionService.cardPreAuthIncrementedNext({ transactionId, preAuthId, newAmountAuthorized, transaction });
            },
          );

          connection.on(
            'CardPreAuthReleased',
            (
              transactionId: number,
              transactionNumber: number,
              sequenceNumber: number,
              preAuthId: number,
              transaction: Transaction | null,
            ) => {
              AppLogger.info(`Pre-Auth released for ${transactionId} (${transactionNumber}/${sequenceNumber})`);
              appSubscriptionService.cardPreAuthReleasedNext({ transactionId, preAuthId, transaction });
            },
          );

          connection.on('NewMenuVersionData', (siteId: number, menuVersion: MenuVersion) => {
            console.info('GOT NEW MENU VERSION', JSON.stringify(menuVersion));
            //store.dispatch(setMenuItems(menuVersion.menu));
            //store.dispatch(setMenuItemCondiments(menuVersion.condiments));
            store.dispatch(setMenuVersion(menuVersion));
          });

          connection.on('NewPackageData', (siteId: number, packages: string) => {
            console.info('GOT NEW PACKAGE DATA', packages);
            store.dispatch(setPackages(JSON.parse(packages)));
          });

          connection.on('NewUnitOfSaleData', (siteId: number, uos: string) => {
            console.info('GOT NEW UOS', JSON.stringify(uos));
            store.dispatch(setUnitOfSales(JSON.parse(uos)));
          });

          connection.on('NewLayoutData', (siteId: number, layoutDefinitions: string) => {
            console.info('GOT NEW LAYOUT DEFINITIONS', JSON.stringify(layoutDefinitions));
            store.dispatch(setLayoutDefinitions(JSON.parse(layoutDefinitions)));
          });

          connection.on('NewCondimentData', (siteId: number, condiments: string) => {
            console.info('GOT NEW CONDIMENT', JSON.stringify(condiments));
            store.dispatch(setMenuItemCondiments(JSON.parse(condiments)));
          });

          connection.on('NewTenderMediaData', (siteId: number, tenders: string) => {
            console.info('GOT NEW TENDERS', JSON.stringify(tenders));
            store.dispatch(setTenderMedias(JSON.parse(tenders)));
          });

          connection.on(
            'StockLevelChanged',
            (menuItemId: number, currentLevel: number, lowStockWarning: boolean, lowStockThreshold: number | null) => {
              console.info('STOCK LEVEL CHANGED', { menuItemId, currentLevel, lowStockWarning, lowStockThreshold });
              store.dispatch(
                updateStockLevel({
                  menuItemId,
                  currentLevel,
                  lowStockThreshold: lowStockThreshold ?? 0,
                }),
              );
            },
          );

          connection.on('Test', () => {
            appSubscriptionService.testNext(new Date().getTime());
          });

          connection.on('NotifyTillSessionBlockNewTransactions', (workstationSessionId: number) => {
            console.info('WORKSTATION IS BLOCKED', workstationSessionId);
            store.dispatch(setWorkstationBlocked(true));
          });
          connection.on('NotifyTillSessionClosed', (workstationSessionId: number) => {
            console.info('WORKSTATION IS BLOCKED', workstationSessionId);
            store.dispatch(setWorkstationClosed(true));
          });

          connection.on('FailedDispensePrintsChanged', () => {
            AppLogger.info('FailedDispensePrintsChanged event received');
            appSubscriptionService.failedDispensePrintsChangedNext();
          });

          connection.on('DispenseLogCreated', (id: number, orderDeviceId: number, transactionId: number, transactionNumber: number) => {
            AppLogger.info('DispenseLogCreated event received', { id, orderDeviceId, transactionId, transactionNumber });
            appSubscriptionService.dispenseLogCreatedNext({ id, orderDeviceId, transactionId, transactionNumber });
            store.dispatch(incrementPendingDispenseCount());
            fetchOutstandingDispenseCount(store);
          });

          connection.on(
            'DispenseLogStateChanged',
            (id: number, orderDeviceId: number, transactionId: number, transactionNumber: number, state: string) => {
              AppLogger.info('DispenseLogStateChanged event received', { id, orderDeviceId, transactionId, transactionNumber, state });
              appSubscriptionService.dispenseLogStateChangedNext({ id, orderDeviceId, transactionId, transactionNumber, state });

              // Deduplicate: skip if we already processed this exact state for this log
              const lastState = lastProcessedDispenseState.get(id);
              if (lastState === state) return;
              lastProcessedDispenseState.set(id, state);

              // If state is 'Served' (3), decrement the count and clean up
              if (state === 'Served' || state === '3') {
                store.dispatch(decrementPendingDispenseCount());
                store.dispatch(decrementReadyToServeCount());
                lastProcessedDispenseState.delete(id);
              }
              // If state changed to 'ReadyToServe' (2), increment ready count
              if (state === 'ReadyToServe' || state === '2') store.dispatch(incrementReadyToServeCount());
              fetchOutstandingDispenseCount(store);
            },
          );

          connection.on('DispenseLogVoided', (id: number, orderDeviceId: number, transactionId: number, transactionNumber: number) => {
            AppLogger.info('DispenseLogVoided event received', { id, orderDeviceId, transactionId, transactionNumber });
            appSubscriptionService.dispenseLogVoidedNext({ id, orderDeviceId, transactionId, transactionNumber });
          });

          connection.on(
            'DispenseLogVoidAcknowledged',
            (id: number, orderDeviceId: number, transactionId: number, transactionNumber: number) => {
              AppLogger.info('DispenseLogVoidAcknowledged event received', { id, orderDeviceId, transactionId, transactionNumber });
              appSubscriptionService.dispenseLogVoidAcknowledgedNext({ id, orderDeviceId, transactionId, transactionNumber });
              store.dispatch(decrementPendingDispenseCount());
              store.dispatch(decrementReadyToServeCount());
              lastProcessedDispenseState.delete(id);
              fetchOutstandingDispenseCount(store);
            },
          );

          connection.on('DispenseLogOverdue', (orderDeviceId: number) => {
            AppLogger.warn('DispenseLogOverdue event received for order device', orderDeviceId);
            appSubscriptionService.dispenseLogOverdueNext({ orderDeviceId });
            store.dispatch(addOverdueOrderDevice(orderDeviceId));
          });

          connection.on('DispenseComplete', (e: DispenseCompleteEvent) => {
            AppLogger.info('DispenseComplete event received', e);
            appSubscriptionService.dispenseCompleteNext(e);
            fetchOutstandingDispenseCount(store);
          });

          connection.on('DispenseReady', (e: DispenseCompleteEvent) => {
            AppLogger.info('DispenseReady event received', e);
            appSubscriptionService.dispenseReadyNext(e);
          });

          connection.on('OutstandingDispenseChanged', () => {
            AppLogger.info('OutstandingDispenseChanged event received');
            fetchOutstandingDispenseCount(store);
          });

          connection.on(
            'CardPaymentLastResultStatus',
            (
              terminalNumber: string,
              transactionNumber: number,
              amount: number,
              success: boolean,
              operatorName: string,
              terminalName: string,
            ) => {
              appSubscriptionService.cardResultStatusNext({
                success,
                terminalId: terminalNumber,
                transactionNumber: transactionNumber,
                lastAmount: amount,
                dateTimeAdded: new Date().getTime(),
                operatorName,
                terminalName,
              });
            },
          );

          connection.onclose((error) => {
            AppLogger.error('SignalR error', error);
            store.dispatch(setSignalrStatus(false));
            store.dispatch(
              showErrorDialog({
                message: 'Disconnected from POS Engine',
                dismissible: false,
                state: {
                  type: 'signalr-disconnected',
                },
                alternateButtonText: 'Reboot',
                showAlternateButton: true,
              }),
            );
          });

          connection.onreconnecting((error) => {
            AppLogger.error('SignalR reconnecting', error);
            store.dispatch(
              showErrorDialog({
                message: `Disconnected from POS Engine<br /><br />Retrying every second`,
                dismissible: false,
              }),
            );
          });

          connection.onreconnected(() => {
            AppLogger.info('SignalR reconnected');
            connection?.invoke('Connect').then((data: boolean) => {
              if (data === true) {
                AppLogger.info('Reconnected to POS SignalR with Workstation ACK');
                store.dispatch(setSignalrStatus(true));
                store.dispatch(clearErrorDialog());
                // Fetch initial pending dispense count
                fetchPendingDispenseCount(store);
              } else AppLogger.warn('SignalR Failed to ack');
            });
          });
        }

        if (connection.state === HubConnectionState.Disconnected) {
          connection.start().then(() => {
            AppLogger.info('Connected to SignalR');

            connection?.invoke('Connect').then((data: boolean) => {
              if (data === true) {
                AppLogger.info('Connected to POS SignalR with Workstation ACK');
                store.dispatch(setSignalrStatus(true));
                store.dispatch(clearErrorDialog());
                // Fetch initial pending dispense count
                fetchPendingDispenseCount(store);
              } else AppLogger.warn('SignalR Failed to ack');
            });
          });
        } else {
          connection.stop().then(() => {
            if (connection === undefined) return;
            connection.start().then(() => {
              AppLogger.info('Connected to SignalR');

              connection?.invoke('Connect').then((data: boolean) => {
                if (data === true) {
                  AppLogger.info('Connected to POS SignalR with Workstation ACK');
                  store.dispatch(setSignalrStatus(true));
                  store.dispatch(clearErrorDialog());
                  // Fetch initial pending dispense count
                  fetchPendingDispenseCount(store);
                } else AppLogger.warn('SignalR Failed to ack');
              });
            });
          });
        }
      }
    } else if (action.type === 'signalr/disconnect') {
      if (connection !== undefined) connection.stop().then(() => store.dispatch(setSignalrStatus(false)));
    } else if (action.type === 'signalr/add-item-to-basket') {
      // do something
      const addPayload = action.payload;
      const transactionId = addPayload.transactionId;
      const model = addPayload.model;

      // AddItemToTransaction
      if (connection !== undefined) {
        const localC: HubConnection = connection;
        AppLogger.info('Adding item to transaction', transactionId, model);
        localC
          .invoke('AddItemToTransaction', transactionId, model)
          .then((data: WebsocketResult<Transaction>) => {
            console.info('websocket result', data);
            if (data.success) {
              const d = data.data;
              store.dispatch(setTransaction([d]));
              AppLogger.info('Added item to transaction', d);
              appSubscriptionService.addToBasketCompleteNext({ success: true });
            } else {
              AppLogger.error('SignalR error', data.error, data.errorCode);

              // Check if error code is 428 (low quantity warning)
              if (data.errorCode === 428) {
                store.dispatch(
                  showErrorDialog({
                    message: `Low Quantity Warning<br />${data.error}<br /><br />Do you want to continue adding this item?`,
                    dismissible: true,
                    buttonText: 'Cancel',
                    alternateButtonText: 'OK - Add Item',
                    showAlternateButton: true,
                    requireConfirmationOnClose: false,
                    alternateState: {
                      type: 'low-quantity-confirm',
                      originalPayload: addPayload,
                    },
                  }),
                );
              } else {
                store.dispatch(
                  showErrorDialog({
                    message: `Failed to add item to transaction<br />${data.error}`,
                    dismissible: true,
                  }),
                );
              }
              appSubscriptionService.addToBasketCompleteNext({ success: false });
            }
          })
          .catch((error) => {
            AppLogger.error('SignalR invoke error', error);
            store.dispatch(
              showErrorDialog({
                message: 'Failed to add item to basket. Please try again.',
                dismissible: true,
              }),
            );
            appSubscriptionService.addToBasketCompleteNext({ success: false });
          });
      }
    }
    next(action);
  };
};

export default signalrMiddleware;
